<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, toRaw, watch } from 'vue'
import { TimelineEngine } from '../timeline/TimelineEngine'
import type { MarketFlowData, TimelineSample, VisualizationConfig } from '../types/market'
import { FundsFlowVisualization } from '../visualization/FundsFlowVisualization'
import type { RenderContext } from '../render/RenderContext'
import { EXPORT_END_HOLD_DURATION_MS, resolveExportFrameTiming } from '../render/ExportFrameTiming'

export interface PlaybackStatus {
  playing: boolean
  elapsedMs: number
  durationMs: number
  speed: number
  frameIndex: number
  time: string
}

const props = defineProps<{
  data: MarketFlowData
  config: VisualizationConfig
  renderContext: RenderContext
}>()

const emit = defineEmits<{
  ready: []
  status: [status: PlaybackStatus]
}>()

const canvas = ref<HTMLCanvasElement>()
const query = new URLSearchParams(window.location.search)
const isExportMode = query.get('export') === '1'
const performanceMonitorEnabled = import.meta.env.DEV && !isExportMode && query.get('perf') === '1'
const performanceStats = reactive({ fps: 0, frameMs: 0, physicsMs: 0, renderMs: 0, bubbles: 0, alpha: 0 })
let timeline: TimelineEngine | undefined
let visualization: FundsFlowVisualization | undefined
let latestSample: TimelineSample | undefined
let renderLoop = 0
let autoPlayTimer = 0
let unsubscribe: (() => void) | undefined
let needsRender = true
let lastStatusEmit = 0
let lastRenderTimestamp = 0
let lastPerformanceEmit = 0
let smoothedFps = 60

const aspectStyle = computed(() => ({
  aspectRatio: `${props.config.videoWidth} / ${props.config.videoHeight}`,
}))

function emitStatus(sample: TimelineSample): void {
  if (!timeline) return
  emit('status', {
    playing: timeline.isPlaying,
    elapsedMs: sample.elapsedMs,
    durationMs: sample.durationMs,
    speed: timeline.playbackSpeed,
    frameIndex: sample.leftIndex,
    time: props.data.frames[sample.leftIndex]?.time ?? '--:--',
  })
}

function animationLoop(timestamp: number): void {
  timeline?.advance(timestamp)
  if (needsRender && latestSample && visualization) {
    const frameStartedAt = performanceMonitorEnabled ? performance.now() : 0
    const measurement = visualization.render(latestSample)
    needsRender = false
    if (performanceMonitorEnabled) {
      const frameMs = performance.now() - frameStartedAt
      if (lastRenderTimestamp > 0) {
        const instantaneousFps = 1000 / Math.max(1, timestamp - lastRenderTimestamp)
        smoothedFps += (instantaneousFps - smoothedFps) * 0.12
      }
      lastRenderTimestamp = timestamp
      if (timestamp - lastPerformanceEmit >= 200) {
        performanceStats.fps = smoothedFps
        performanceStats.frameMs = frameMs
        performanceStats.physicsMs = measurement.physicsMs
        performanceStats.renderMs = measurement.renderMs
        performanceStats.bubbles = measurement.bubbleCount
        performanceStats.alpha = measurement.simulationAlpha
        lastPerformanceEmit = timestamp
      }
    }
  }
  renderLoop = requestAnimationFrame(animationLoop)
}

function play(): void {
  timeline?.play()
  if (timeline) emitStatus(timeline.sample())
}

function pause(): void {
  timeline?.pause()
  if (timeline) emitStatus(timeline.sample())
}

function restart(): void {
  timeline?.restart()
}

function previousFrame(): void {
  timeline?.previousFrame()
  if (timeline) emitStatus(timeline.sample())
}

function nextFrame(): void {
  timeline?.nextFrame()
  if (timeline) emitStatus(timeline.sample())
}

function seekMs(value: number): void {
  timeline?.seekMs(value)
  if (timeline) emitStatus(timeline.sample())
}

function seekFrame(value: number): void {
  timeline?.seekFrame(value)
  if (timeline) emitStatus(timeline.sample())
}

function setSpeed(value: number): void {
  timeline?.setSpeed(value)
  if (timeline) emitStatus(timeline.sample())
}

onMounted(async () => {
  if (!canvas.value) return
  await document.fonts.ready
  timeline = new TimelineEngine(props.data.frames.length, props.config.marketMinuteDuration)
  const rawData = toRaw(props.data)
  const rawConfig = toRaw(props.config)
  visualization = new FundsFlowVisualization(
    canvas.value,
    rawData,
    rawConfig,
    props.renderContext.title,
    isExportMode,
    performanceMonitorEnabled,
  )
  unsubscribe = timeline.subscribe((sample) => {
    latestSample = sample
    needsRender = true
    const now = performance.now()
    if (now - lastStatusEmit >= 200) {
      emitStatus(sample)
      lastStatusEmit = now
    }
  })
  visualization.render(timeline.sample())
  needsRender = false

  const endHoldDurationMs = isExportMode && props.renderContext.mode !== 'preview'
    ? EXPORT_END_HOLD_DURATION_MS
    : 0
  const exportDurationMs = timeline.durationMs + endHoldDurationMs

  window.marketFlowExport = {
    ready: true,
    durationMs: exportDurationMs,
    timelineDurationMs: timeline.durationMs,
    endHoldDurationMs,
    fps: props.config.fps,
    width: props.config.videoWidth,
    height: props.config.videoHeight,
    renderMode: props.renderContext.mode,
    date: props.data.date,
    firstTime: props.data.frames[0]?.time ?? '',
    lastTime: props.data.frames.at(-1)?.time ?? '',
    title: props.renderContext.title,
    sectorCount: props.data.frames[0]?.sectors.length ?? 0,
    physicsSnapshot: () => visualization?.getPhysicsSnapshot() ?? [],
    renderFrame: async (frameNumber: number) => {
      if (!timeline || !visualization) return
      const timing = resolveExportFrameTiming(
        frameNumber,
        props.config.fps,
        timeline.durationMs,
        endHoldDurationMs,
      )
      const sample = timeline.sampleAt(timing.timelineElapsedMs)
      visualization.renderExportFrame(sample, frameNumber, {
        freezePhysics: timing.isEndHold,
        effectsTimelineMs: timing.effectsElapsedMs,
        allowChampionParticleEmission: !timing.isEndHold,
      })
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
    },
  }

  if (!isExportMode) {
    renderLoop = requestAnimationFrame(animationLoop)
    autoPlayTimer = window.setTimeout(() => play(), 520)
  }
  emit('ready')
})

onBeforeUnmount(() => {
  unsubscribe?.()
  timeline?.destroy()
  visualization?.destroy()
  cancelAnimationFrame(renderLoop)
  window.clearTimeout(autoPlayTimer)
  delete window.marketFlowExport
})

defineExpose({ play, pause, restart, previousFrame, nextFrame, seekMs, seekFrame, setSpeed })

watch(props.config, () => {
  needsRender = true
}, { deep: true })
</script>

<template>
  <div class="canvas-shell" :style="aspectStyle">
    <canvas ref="canvas" class="market-canvas" aria-label="A股板块分钟级主力资金流向动态画布" />
    <output v-if="performanceMonitorEnabled" class="performance-monitor">
      <span>FPS {{ performanceStats.fps.toFixed(1) }}</span>
      <span>Frame {{ performanceStats.frameMs.toFixed(2) }}ms</span>
      <span>Physics {{ performanceStats.physicsMs.toFixed(2) }}ms</span>
      <span>Render {{ performanceStats.renderMs.toFixed(2) }}ms</span>
      <span>Bubbles {{ performanceStats.bubbles }}</span>
      <span>Alpha {{ performanceStats.alpha.toFixed(3) }}</span>
    </output>
  </div>
</template>

<style scoped>
.canvas-shell {
  position: relative;
  height: min(100vh, 100dvh);
  max-width: 100vw;
  overflow: hidden;
  background: var(--bg-primary);
  box-shadow: 0 40px 120px rgb(0 0 0 / 55%);
}

.market-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.performance-monitor {
  position: absolute;
  top: 10px;
  left: 10px;
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 3px 12px;
  padding: 8px 10px;
  color: #cfe7ef;
  background: rgb(3 8 11 / 82%);
  border: 1px solid rgb(125 173 193 / 22%);
  border-radius: 7px;
  font: 11px/1.25 Consolas, monospace;
  pointer-events: none;
}
</style>
